import { API_BASE } from '../../config.js';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../i18n/formatters';

// Brightivity: the printables fork behind the front door (brightivity.dev/printables/<x> = /api/<x>).
const PRINTABLES_API = `${API_BASE}/printables`;

const PRIORITY_CATEGORIES = [
  {
    id: 'a0e4fb6d-a0c1-43fb-a541-b0534d0262e7',
    i18nKey: 'classroomDisplays',
    color: '#7F4BF6',
    gradient: 'linear-gradient(135deg, #5F2FD1, #7F4BF6)',
    rgb: '139,92,246',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
  {
    id: 'a0e4fb6e-90a1-4c74-9586-3e9027400b1f',
    i18nKey: 'parentCommunication',
    color: '#7F4BF6',
    gradient: 'linear-gradient(135deg, #5F2FD1, #7F4BF6)',
    rgb: '245,158,11',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M22 7l-10 7L2 7" />
      </svg>
    ),
  },
  {
    id: 'a0e4d6c1-63e6-46bf-aa61-f411a4f01d80',
    i18nKey: 'seasonalEvents',
    color: '#f97316',
    gradient: 'linear-gradient(135deg, #ea580c, #f97316)',
    rgb: '249,115,22',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
        <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
      </svg>
    ),
  },
  {
    id: 'a180bb58-98d2-44f3-9f60-7e5d7630a228',
    i18nKey: 'fundraising',
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #db2777, #ec4899)',
    rgb: '236,72,153',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6" />
        <path d="M2 8h20v4H2z" />
        <path d="M12 2v18" />
        <path d="M12 2a3 3 0 00-3 3h6a3 3 0 00-3-3z" />
      </svg>
    ),
  },
  {
    id: 'a0e4d791-e811-476b-901b-417cd16a240e',
    i18nKey: 'recognitionBoards',
    color: '#26C774',
    gradient: 'linear-gradient(135deg, #059669, #26C774)',
    rgb: '16,185,129',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9H4.5a2.5 2.5 0 010-5C7 4 7 7 7 7" />
        <path d="M18 9h1.5a2.5 2.5 0 000-5C17 4 17 7 17 7" />
        <path d="M4 22h16" />
        <path d="M10 22V8a6 6 0 00-3-5h10a6 6 0 00-3 5v14" />
        <path d="M14 13h-4" />
      </svg>
    ),
  },
  {
    id: 'a0e4fb6e-a376-4330-841e-b0fded414e5d',
    i18nKey: 'mathManipulatives',
    color: '#1AB0F2',
    gradient: 'linear-gradient(135deg, #0E8FCC, #1AB0F2)',
    rgb: '59,130,246',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <path d="M8 6h8M8 10h8M8 14h4" />
        <circle cx="16" cy="16" r="2" />
      </svg>
    ),
  },
  {
    id: 'a180c025-c022-460a-aecd-06d108df82f6',
    i18nKey: 'specialtyApplications',
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #0891b2, #06b6d4)',
    rgb: '6,182,212',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19.439 7.85c-.049 3.689-2.19 6.972-5.439 8.35-3.249-1.378-5.39-4.661-5.439-8.35A2.56 2.56 0 0111.07 5.5h1.86a2.56 2.56 0 012.509 2.35z" />
        <path d="M12 2v3.5" />
        <path d="M3 14h3M18 14h3" />
        <path d="M5 5l2.12 2.12M16.88 7.12L19 5" />
      </svg>
    ),
  },
  {
    id: 'a0e4fb6d-b693-40c3-90f8-141a9d4175fc',
    i18nKey: 'bannersHeaders',
    color: '#d946ef',
    gradient: 'linear-gradient(135deg, #c026d3, #d946ef)',
    rgb: '217,70,239',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <path d="M4 22v-7" />
      </svg>
    ),
  },
  {
    id: 'a0e4d6c1-62fb-4fb8-b2ea-18632c5b2b07',
    i18nKey: 'safetyHealth',
    color: '#FF5151',
    gradient: 'linear-gradient(135deg, #D93A3A, #FF5151)',
    rgb: '239,68,68',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 'a180bec6-1644-4266-944f-20fee2655e1a',
    i18nKey: 'adaAccessibility',
    color: '#14b8a6',
    gradient: 'linear-gradient(135deg, #0d9488, #14b8a6)',
    rgb: '20,184,166',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="4.5" r="2.5" />
        <path d="M12 7v5" />
        <path d="M8 9h8" />
        <path d="M9 12l-2 7h2l3-4 3 4h2l-2-7" />
      </svg>
    ),
  },
];

function CategoryCard({ cat, thumbnailUrl, thumbnailLoading, posterCount, onClick }) {
  const { t } = useTranslation('printables');
  const [hovered, setHovered] = useState(false);
  const hasThumbnail = !!thumbnailUrl;
  const hasPosters = posterCount > 0;

  const cardBg = hasThumbnail
    ? `url(${thumbnailUrl})`
    : `linear-gradient(135deg, rgba(${cat.rgb},0.12) 0%, rgba(${cat.rgb},0.04) 100%)`;

  return (
    <div
      onClick={() => onClick(cat.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        height: 180,
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        border: `1px solid ${hovered ? `rgba(${cat.rgb},0.35)` : 'rgba(27,37,80,0.076)'}`,
        backgroundImage: hasThumbnail ? cardBg : 'none',
        backgroundColor: hasThumbnail ? '#EEF0F6' : 'transparent',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered
          ? `0 8px 24px rgba(${cat.rgb},0.3), 0 0 40px rgba(${cat.rgb},0.08)`
          : '0 2px 8px rgba(27,37,80,0.05)',
        transition: 'all 0.25s ease',
      }}
    >
      {/* Gradient overlay — always present */}
      <div style={{
        position: 'absolute', inset: 0,
        background: hasThumbnail
          ? 'linear-gradient(to bottom, rgba(10,15,43,0.1) 0%, rgba(10,15,43,0.82) 100%)'
          : `linear-gradient(135deg, rgba(${cat.rgb},0.12) 0%, rgba(${cat.rgb},0.04) 100%)`,
        zIndex: 1,
      }} />

      {/* Loading pulse for thumbnail */}
      {thumbnailLoading && !hasThumbnail && hasPosters && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, rgba(${cat.rgb},0.1) 0%, rgba(${cat.rgb},0.03) 100%)`,
          animation: 'pulse 1.5s ease-in-out infinite',
          zIndex: 0,
        }} />
      )}

      {/* Content — positioned at bottom */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '16px 18px',
        zIndex: 2,
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        {/* Icon + Name row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: cat.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 0 12px rgba(${cat.rgb},0.3)`,
            border: '1px solid rgba(27,37,80,0.19)',
          }}>
            {cat.icon}
          </div>
          <span style={{
            fontSize: '0.95rem', fontWeight: 700, color: hasThumbnail ? '#FFFFFF' : '#1B2550',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textShadow: hasThumbnail ? '0 1px 3px rgba(0,0,0,0.5)' : 'none',
          }}>
            {t(`landing.categories.${cat.i18nKey}.name`)}
          </span>
        </div>

        {/* Description */}
        <p style={{
          margin: 0, fontSize: '0.78rem', lineHeight: 1.4,
          color: hasThumbnail ? 'rgba(255,255,255,0.85)' : 'rgba(27,37,80,0.6)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textShadow: hasThumbnail ? '0 1px 2px rgba(0,0,0,0.4)' : 'none',
        }}>
          {t(`landing.categories.${cat.i18nKey}.description`)}
        </p>

        {/* Badge + Arrow row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {hasPosters ? (
            <span style={{
              fontSize: '0.68rem', fontWeight: 600,
              color: `rgba(${cat.rgb},1)`,
              background: `rgba(${cat.rgb},0.15)`,
              padding: '2px 8px', borderRadius: 8,
              letterSpacing: '0.02em',
            }}>
              {t('landing.posterCount', { count: posterCount })}
            </span>
          ) : (
            <span style={{
              fontSize: '0.68rem', fontWeight: 600,
              color: 'rgba(27,37,80,0.332)',
              background: '#FFFFFF',
              padding: '2px 8px', borderRadius: 8,
              letterSpacing: '0.02em',
            }}>
              {t('landing.comingSoon')}
            </span>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            style={{ opacity: hovered ? 0.8 : 0.35, transition: 'opacity 0.2s ease', transform: hovered ? 'translateX(2px)' : 'none' }}>
            <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function PrintablesLanding({ totalPosters, onCategorySelect, onBrowseAll }) {
  const { t } = useTranslation('printables');
  const [thumbnails, setThumbnails] = useState({});
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  // Fetch live poster counts + representative thumbnails for all categories (parallel)
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    Promise.all(
      PRIORITY_CATEGORIES.map(cat =>
        fetch(`${PRINTABLES_API}/posters?category_id=${cat.id}&limit=1&sort=popular`)
          .then(res => res.ok ? res.json() : null)
          .then(json => {
            const total = json?.data?.total ?? 0;
            const poster = json?.data?.posters?.[0];
            return {
              id: cat.id,
              count: total,
              url: poster?.thumbnailUrl || null,
            };
          })
          .catch(() => ({ id: cat.id, count: 0, url: null }))
      )
    ).then(results => {
      const thumbMap = {};
      const countMap = {};
      results.forEach(r => {
        countMap[r.id] = r.count;
        if (r.url) thumbMap[r.id] = r.url;
      });
      setThumbnails(thumbMap);
      setCounts(countMap);
      setLoading(false);
    });
  }, []);

  return (
    <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      {/* Hero Section */}
      <div style={{
        position: 'relative',
        padding: '24px 28px 22px',
        marginBottom: 8,
        borderRadius: 18,
        background: 'linear-gradient(135deg, rgba(232,104,72,0.06) 0%, rgba(255,255,255,0.9) 40%, rgba(26,176,242,0.04) 100%)',
        border: '1px solid rgba(232,104,72,0.1)',
        overflow: 'hidden',
        display: 'flex', alignItems: 'center', gap: 24,
        minHeight: 170,
      }}>
        {/* Animated gradient orbs */}
        <div style={{
          position: 'absolute', top: -60, right: 60,
          width: 250, height: 250, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,104,72,0.1) 0%, transparent 65%)',
          animation: 'heroFloat 8s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: -50, left: '30%',
          width: 200, height: 200, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(26,176,242,0.06) 0%, transparent 65%)',
          animation: 'heroFloat 10s ease-in-out infinite reverse',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '50%', right: '15%',
          width: 140, height: 140, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(217,70,239,0.05) 0%, transparent 65%)',
          animation: 'heroFloat 12s ease-in-out infinite',
          animationDelay: '-4s',
          pointerEvents: 'none',
        }} />

        {/* Shimmer line top */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 1,
          background: 'linear-gradient(90deg, transparent 0%, rgba(232,104,72,0.25) 30%, rgba(217,70,239,0.15) 70%, transparent 100%)',
          pointerEvents: 'none',
        }} />
        {/* Shimmer line bottom */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 1,
          background: 'linear-gradient(90deg, transparent 0%, rgba(26,176,242,0.1) 50%, transparent 100%)',
          pointerEvents: 'none',
        }} />

        {/* Left: Text content */}
        <div style={{ position: 'relative', zIndex: 2, flex: '1 1 auto', minWidth: 0 }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 8,
            background: 'rgba(232,104,72,0.08)',
            border: '1px solid rgba(232,104,72,0.15)',
            marginBottom: 10,
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#ff9070', boxShadow: '0 0 6px rgba(255,144,112,0.6)' }} />
            <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#C42F2F', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('landing.hero.badge')}
            </span>
          </div>

          <h1 style={{
            margin: '0 0 6px', fontSize: '1.5rem', fontWeight: 800,
            background: 'linear-gradient(135deg, #ff9070, #e86848, #ff7050)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            lineHeight: 1.15, letterSpacing: '-0.3px',
          }}>
            {t('landing.hero.title')}
          </h1>
          <p style={{
            margin: '0 0 14px', fontSize: '0.82rem', lineHeight: 1.5,
            color: 'rgba(27,37,80,0.65)', maxWidth: 400,
          }}>
            {t('landing.hero.subtitle')}
          </p>

          {/* Button + stats row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <button
              onClick={onBrowseAll}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '7px 16px', borderRadius: 10,
                background: 'linear-gradient(135deg, #FF5151, #FF7A6B)',
                border: '1px solid rgba(232,104,72,0.25)',
                color: '#FFFFFF', fontSize: '0.78rem', fontWeight: 800,
                cursor: 'pointer', transition: 'all 0.2s ease',
                letterSpacing: '0.02em',
                boxShadow: '0 2px 12px rgba(232,104,72,0.08)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #D93A3A, #FF5151)';
                e.currentTarget.style.borderColor = 'rgba(232,104,72,0.5)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(232,104,72,0.2)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #FF5151, #FF7A6B)';
                e.currentTarget.style.borderColor = 'rgba(232,104,72,0.25)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(232,104,72,0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {t('landing.hero.browseAll')}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <div style={{ width: 1, height: 20, background: '#FFFFFF' }} />

            <div style={{ display: 'flex', gap: 14 }}>
              {[
                { value: totalPosters > 0 ? formatNumber(totalPosters) : '—', label: t('landing.hero.statPosters') },
                { value: '10', label: t('landing.hero.statCollections') },
                { value: t('landing.hero.gradesValue'), label: t('landing.hero.statGrades') },
              ].map(stat => (
                <div key={stat.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'rgba(27,37,80,0.712)', lineHeight: 1 }}>{stat.value}</div>
                  <div style={{ fontSize: '0.55rem', fontWeight: 500, color: 'rgba(27,37,80,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Fanned poster mosaic */}
        {Object.keys(thumbnails).length > 0 && (
          <div style={{
            position: 'relative', zIndex: 1,
            width: 220, height: 150, flexShrink: 0,
          }}>
            {/* Glow behind posters */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 180, height: 120, borderRadius: '50%',
              background: 'radial-gradient(ellipse, rgba(232,104,72,0.12) 0%, transparent 70%)',
              filter: 'blur(20px)',
              pointerEvents: 'none',
            }} />
            {Object.values(thumbnails).slice(0, 5).map((url, i) => {
              const rotations = [-8, -3, 2, 7, 12];
              const offsets = [0, 36, 72, 108, 144];
              const zIndexes = [1, 2, 3, 4, 5];
              return (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: offsets[i],
                    top: '50%',
                    transform: `translateY(-50%) rotate(${rotations[i]}deg)`,
                    width: 72,
                    height: 100,
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '2px solid rgba(27,37,80,0.114)',
                    boxShadow: '0 4px 16px rgba(27,37,80,0.17)',
                    zIndex: zIndexes[i],
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    animation: `posterEntrance 0.5s ease-out ${i * 0.08}s both`,
                  }}
                >
                  <img
                    src={url}
                    alt=""
                    draggable={false}
                    loading="lazy"
                    style={{
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                      imageRendering: 'auto',
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Featured Collections Label */}
      <div style={{
        fontSize: '0.65rem', fontWeight: 600,
        color: 'rgba(27,37,80,0.237)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        padding: '12px 4px 10px',
      }}>
        {t('landing.featuredCollections')}
      </div>

      {/* Category Cards Grid */}
      <div data-tour="printables-collections" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 16,
        paddingBottom: 24,
      }}>
        {PRIORITY_CATEGORIES.map(cat => (
          <CategoryCard
            key={cat.id}
            cat={cat}
            thumbnailUrl={thumbnails[cat.id] || null}
            thumbnailLoading={loading}
            posterCount={counts[cat.id] ?? 0}
            onClick={onCategorySelect}
          />
        ))}
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes heroFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-8px, 6px) scale(1.05); }
        }
        @keyframes posterEntrance {
          from { opacity: 0; transform: translateY(-50%) rotate(0deg) scale(0.8); }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
